import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Loader2 } from 'lucide-react';

interface AvailableCoin {
  pair: string;
  symbol: string;
  name: string;
}

interface CoinSearchProps {
  currentCoins: string[];
  onAddCoin: (pair: string, symbol: string, name: string) => void;
  maxCoins: number;
  apiUrl: string;
}

const CoinSearch: React.FC<CoinSearchProps> = ({ currentCoins, onAddCoin, maxCoins, apiUrl }) => {
  const [query, setQuery] = useState('');
  const [available, setAvailable] = useState<AvailableCoin[]>([]);
  const [filtered, setFiltered] = useState<AvailableCoin[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchAvailable = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${apiUrl}/api/coins/available`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setAvailable(data);
        } else {
          console.error('Available coins response is not an array:', data);
          setAvailable([]);
        }
      } catch (error) {
        console.error('Error fetching available coins:', error);
        setAvailable([]);
      }
      setLoading(false);
    };
    fetchAvailable();
  }, [apiUrl]);

  useEffect(() => {
    if (!Array.isArray(available) || available.length === 0) {
      setFiltered([]);
      return;
    }

    if (!query.trim()) {
      const popular = ['BTC', 'ETH', 'XRP', 'SOL', 'ADA', 'DOGE', 'DOT', 'LINK', 'AVAX', 'UNI', 'MATIC', 'LTC', 'ATOM', 'AAVE', 'ARB'];
      const popularCoins = available.filter(c => popular.includes(c.symbol) && !currentCoins.includes(c.pair));
      setFiltered(popularCoins.slice(0, 10));
      return;
    }

    const q = query.toUpperCase();
    const results = available.filter(coin =>
      !currentCoins.includes(coin.pair) &&
      (coin.symbol.includes(q) || coin.name.toUpperCase().includes(q) || coin.pair.includes(q))
    );
    setFiltered(results.slice(0, 10));
  }, [query, available, currentCoins]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAdd = (coin: AvailableCoin) => {
    if (currentCoins.length >= maxCoins) {
      alert(`Maximum ${maxCoins} coins allowed. Remove one first.`);
      return;
    }
    onAddCoin(coin.pair, coin.symbol, coin.name);
    setQuery('');
    setIsOpen(false);
  };

  const atLimit = currentCoins.length >= maxCoins;

  return (
    <div ref={wrapperRef} className="relative">
      <div className={`flex items-center gap-2 bg-gray-800 border rounded-lg px-4 py-2.5 transition-colors ${
        isOpen ? 'border-blue-500' : 'border-gray-700 hover:border-gray-600'
      } ${atLimit ? 'opacity-60' : ''}`}>
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          placeholder={atLimit ? `Maximum ${maxCoins} coins reached` : "Search coins to add... (e.g. SOL, Solana)"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => !atLimit && setIsOpen(true)}
          disabled={atLimit}
          className="bg-transparent text-white placeholder-gray-500 outline-none flex-1 text-sm"
        />
        {loading && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
        <span className="text-xs text-gray-500 flex-shrink-0">
          {currentCoins.length}/{maxCoins}
        </span>
      </div>

      {isOpen && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg shadow-xl max-h-80 overflow-y-auto">
          {!query.trim() && (
            <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-700">
              Popular coins
            </div>
          )}
          {filtered.map((coin) => (
            <button
              key={coin.pair}
              onClick={() => handleAdd(coin)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-700/50 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold text-white">
                  {coin.symbol.slice(0, 2)}
                </div>
                <div>
                  <div className="text-sm font-medium text-white">{coin.symbol}/USD</div>
                  <div className="text-xs text-gray-400">{coin.name}</div>
                </div>
              </div>
              <Plus className="w-4 h-4 text-blue-400" />
            </button>
          ))}
        </div>
      )}

      {isOpen && !loading && filtered.length === 0 && query.trim() && (
        <div className="absolute z-50 mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-4 text-center text-sm text-gray-400">
          No coins found for "{query}"
        </div>
      )}
    </div>
  );
};

export default CoinSearch;
